import { useState } from 'react'
import { useSortable } from '@dnd-kit/react/sortable'
import { GripVertical, Trash2, Lock, LockOpen, Link2, Paperclip, Gauge } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { prerequisiteIds } from '@/lib/formSections'
import { acceptsAllButSections } from '@/lib/formBuilderDnd'
import { attachmentMode } from '@/types/cgt'
import { MultiSelect } from './MultiSelect'
import type { FieldPlacement } from '@/lib/fieldLayout'
import type { FormField, SectionWidth } from './types'
import { FieldPreview } from './FieldPreview'
import { PROCESS_ACCENT } from './field-config'
import { oosGradingType } from '@/lib/oos'

/**
 * Field types whose FieldPreview renders a label directly above a single-line
 * control (Input / SelectTrigger, both `h-10`). Only these need the attachment
 * icon nudged onto the control row — see `alignIconToControl`.
 */
const SINGLE_LINE_CONTROL_TYPES: string[] = ['text', 'number', 'date', 'datetime', 'select']

/**
 * Distance from the top of a stacked preview to the vertical centre of the
 * `h-7` attachment icon once it is sitting on the control row:
 * label (`text-sm leading-none` = 14px) + `space-y-1.5` (6px) + half the
 * control-vs-icon difference ((40 - 28) / 2 = 6px) = 26px.
 */
const CONTROL_ROW_OFFSET = 'mt-[26px]'

/**
 * The same, when the label sits beside the control (z8nrz7djyx): the control
 * starts at the top, so only half the control-vs-icon difference is left.
 */
const SIDE_CONTROL_ROW_OFFSET = 'mt-1.5'

interface Props {
  field: FormField
  /** Label layout and the width the field actually takes; see lib/fieldLayout. */
  placement: FieldPlacement
  index: number
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
  /** Set this field's prerequisite ids (empty → always unlocked). */
  onSetUnlockedBy: (ids: string[]) => void
  /** Flat, ordered list of every field on the form — to offer only earlier fields. */
  allFields: FormField[]
  /** The sortable group (section container) this field belongs to, e.g. 'sec:<id>'. */
  group: string
  /** Id of the owning section, carried in drag data for cross-section moves. */
  sectionId: string
  /** Visibility click-to-pick: a condition row is armed and awaiting a canvas click. */
  pickModeActive?: boolean
  /** Whether this field may be picked as a visibility source while picking. */
  pickEligible?: boolean
  /** Called instead of onSelect when this field is clicked in pick mode. */
  onPick?: () => void
}

export function CanvasField({
  field,
  placement,
  index,
  isSelected,
  onSelect,
  onDelete,
  onSetUnlockedBy,
  allFields,
  group,
  sectionId,
  pickModeActive = false,
  pickEligible = false,
  onPick,
}: Props) {
  const { ref, handleRef, isDragging } = useSortable({
    id: field.id,
    index,
    group,
    // A section dragged over this field must not win the collision, or dnd-kit
    // re-parents the whole section into this one's field list (z8nrz7e1wt).
    accept: acceptsAllButSections,
    data: { origin: 'field', sectionId },
  })

  const [pickerOpen, setPickerOpen] = useState(false)

  const isSequenced = prerequisiteIds(field).length > 0
  const hasVisibility = !!field.visibility
  const hasSpec = !!field.oos && !!oosGradingType(field.type, field.displayFormat)
  const attachment = attachmentMode(field.attachment)
  // For single-line controls the icon is offset onto the control's own centre
  // rather than centred on the whole block, so it stays put when helpText is
  // added underneath. The offset depends on where the label sits: above the
  // control it pushes the control down, beside it it does not. Taller
  // controls (textarea, signature, process) read fine block-centred.
  const alignIconToControl = SINGLE_LINE_CONTROL_TYPES.includes(field.type)
  const labelBeside = !placement.stacked && placement.layout.position !== 'above'
  // Process steps are work, not data capture — they get their own colour so the
  // canvas separates them from ordinary fields at a glance.
  const isProcess = field.type === 'process'

  // Only value-bearing fields BEFORE this one can be prerequisites (tables store
  // values under composite cell keys; sections have no value) — mirrors the
  // Properties-panel field picker.
  const fieldIdx = allFields.findIndex(f => f.id === field.id)
  const earlierFields =
    fieldIdx > 0
      ? allFields
          .slice(0, fieldIdx)
          .filter(
            f =>
              f.type !== 'table' &&
              f.type !== 'section' &&
              // Nothing in the values map to wait on: a text block is fixed
              // text, images and attachments are files, a review is signed later.
              f.type !== 'description' &&
              f.type !== 'image' &&
              f.type !== 'attachment' &&
              f.type !== 'review',
          )
      : []
  const selectedPrereqs = prerequisiteIds(field)

  const FIELD_SPAN: Record<SectionWidth, string> = {
    // Not col-span-12: in a one-column grid (narrow section) that adds implicit tracks.
    full: 'col-span-full',
    half: 'col-span-6',
    third: 'col-span-4',
    quarter: 'col-span-3',
  }
  const spanClass = FIELD_SPAN[placement.width]

  return (
    <div
      ref={ref}
      className={cn(
        'group relative',
        spanClass,
        isDragging && 'opacity-20',
        pickModeActive
          ? pickEligible
            ? 'cursor-crosshair'
            : 'cursor-not-allowed opacity-40'
          : 'cursor-pointer',
      )}
      onClick={() => {
        // While a visibility condition row is armed, a canvas click picks the
        // source field instead of selecting the card.
        if (pickModeActive) {
          if (pickEligible) onPick?.()
          return
        }
        onSelect()
      }}
    >
      {/* Actual form field — pointer-events-none so clicks select the field, not the control */}
      <div
        className={cn(
          'pointer-events-none select-none rounded-lg border p-3 transition-all',
          // Extra left padding clears the spine; overflow-hidden keeps it inside
          // the rounded corners.
          isProcess ? cn('relative overflow-hidden pl-4', PROCESS_ACCENT.surface) : 'bg-card',
          isSelected
            ? isProcess
              ? PROCESS_ACCENT.borderSelected
              : 'border-primary ring-2 ring-primary/20 shadow-sm'
            : isProcess
              ? cn(PROCESS_ACCENT.border, PROCESS_ACCENT.borderHover)
              : 'border-border group-hover:border-primary/40',
          pickModeActive && pickEligible && 'border-primary/60 ring-2 ring-primary/30',
        )}
      >
        {isProcess && (
          <span
            aria-hidden
            className={cn('absolute inset-y-0 left-0 w-[3px]', PROCESS_ACCENT.spine)}
          />
        )}
        {attachment === 'none' ? (
          <FieldPreview field={field} layout={placement.layout} stacked={placement.stacked} />
        ) : (
          // Mirrors the renderer's own layout (FormRenderer's withAttachment):
          // the attachment icon sits beside its field, not floating in a
          // corner, so it reads the same way here as it does when actually
          // filling out the form.
          <div className={cn('flex gap-2', alignIconToControl ? 'items-start' : 'items-center')}>
            <div className='min-w-0 flex-1'>
              <FieldPreview field={field} layout={placement.layout} stacked={placement.stacked} />
            </div>
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
                alignIconToControl && (labelBeside ? SIDE_CONTROL_ROW_OFFSET : CONTROL_ROW_OFFSET),
                attachment === 'required'
                  ? 'border-destructive text-destructive'
                  : 'border-transparent bg-primary/10 text-primary',
              )}
              aria-label={
                attachment === 'required' ? 'Attachment is required' : 'Attachment is enabled'
              }
              title={
                attachment === 'required'
                  ? 'A file attachment is required to complete this field'
                  : 'A file attachment is enabled (optional) for this field'
              }
            >
              <Paperclip className='h-4 w-4' />
            </div>
          </div>
        )}
        {/* The test code the field is connected to (z8nrz7g2wm), beneath it as on
            the filled form. A table shows its codes per cell, in the renderer. */}
        {field.lisTestCode && field.type !== 'table' ? (
          <p className='mt-1 text-xs text-muted-foreground' data-lis-test-code={field.lisTestCode}>
            Test code <span className='font-mono font-medium text-foreground/80'>{field.lisTestCode}</span>
          </p>
        ) : null}
      </div>

      {/* Persistent badges — sequencing lock, visibility rule, specification */}
      {(isSequenced || hasVisibility || hasSpec) && (
        <div
          className={cn(
            'absolute top-2 right-2 z-10 pointer-events-none flex items-center gap-1',
            // Hide while the hover toolbar is showing so they don't overlap
            'group-hover:opacity-0 transition-opacity',
            (isSelected || pickerOpen) && 'opacity-0',
          )}
        >
          {hasSpec && (
            <div
              className='flex h-5 w-5 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary shadow-sm'
              aria-label='Field has a specification'
              title='Values outside the specification are flagged for review'
            >
              <Gauge className='h-3 w-3' />
            </div>
          )}
          {hasVisibility && (
            <div
              className='flex h-5 w-5 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary shadow-sm'
              aria-label='Field has visibility conditions'
              title="Shown/hidden based on other fields' values"
            >
              <Link2 className='h-3 w-3' />
            </div>
          )}
          {isSequenced && (
            <div
              className='flex h-5 w-5 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary shadow-sm'
              aria-label='Field is locked'
              title='Locked until its prerequisite field(s) are filled'
            >
              <Lock className='h-3 w-3' />
            </div>
          )}
        </div>
      )}

      {/* Floating toolbar — shown on hover / always visible when selected */}
      <div
        className={cn(
          'absolute top-2 right-2 z-10 pointer-events-auto',
          'flex items-center gap-0.5 rounded-md border bg-background shadow-sm px-1 py-0.5',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          (isSelected || pickerOpen) && 'opacity-100',
        )}
        onClick={e => e.stopPropagation()}
      >
        <button
          ref={handleRef}
          className='p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing rounded'
          aria-label='Drag to reorder'
        >
          <GripVertical className='h-3.5 w-3.5' />
        </button>
        <div className='w-px h-3 bg-border mx-0.5' />
        <Button
          size='icon'
          variant='ghost'
          className={cn(
            'h-5 w-5',
            isSequenced
              ? 'text-primary hover:text-primary hover:bg-primary/10'
              : 'text-muted-foreground hover:text-foreground',
          )}
          onClick={() => setPickerOpen(o => !o)}
          aria-label='Edit field prerequisites'
          aria-expanded={pickerOpen}
        >
          {isSequenced ? <Lock className='h-3 w-3' /> : <LockOpen className='h-3 w-3' />}
        </Button>
        <Button
          size='icon'
          variant='ghost'
          className='h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          onClick={onDelete}
          aria-label='Delete field'
        >
          <Trash2 className='h-3 w-3' />
        </Button>
      </div>

      {/* Inline prerequisite picker — opened from the lock icon. Absolutely
          positioned so it never shifts the canvas layout; a transparent backdrop
          closes it on outside click. */}
      {pickerOpen && (
        <>
          <button
            type='button'
            className='fixed inset-0 z-20 cursor-default'
            aria-label='Close prerequisite picker'
            onClick={e => {
              e.stopPropagation()
              setPickerOpen(false)
            }}
          />
          <div
            className='absolute top-9 right-2 z-30 w-64 rounded-md border bg-popover p-2 shadow-lg'
            onClick={e => e.stopPropagation()}
          >
            <p className='mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Unlock after other fields are filled
            </p>
            {earlierFields.length === 0 ? (
              <p className='rounded-md bg-muted/50 px-2 py-1.5 text-xs text-muted-foreground'>
                No earlier fields to depend on.
              </p>
            ) : (
              <MultiSelect
                options={earlierFields.map(f => ({ id: f.id, label: f.label || 'Untitled' }))}
                selected={selectedPrereqs}
                onChange={onSetUnlockedBy}
                placeholder='None — always unlocked'
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
